// FS-2026-065: the platform obligation precedes the independent federation gate.
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useAuthContext } from "src/auth/hooks";
import { useTranslate } from "src/locales";
import {
  acceptPlatformPolicy,
  usePendingPlatformPolicy,
} from "src/api/platform-legal";
import { isPlatformPending } from "src/types/platform-legal";
import { LoadingScreen } from "src/components/loading-screen";
import LegalDocumentText from "src/components/legal-document-text/legal-document-text";
import Iconify from "src/components/iconify";
import LegalAcceptanceActions, {
  legalDialogPaperSx,
} from "./legal-acceptance-actions";
import LegalLanguageSelect from "./legal-language-select";

const AccountDelete = lazy(
  () => import("src/sections/player/player-profiledata-delete-profile"),
);
const recoveryPath =
  /^\/(privacy-policy|terms-and-conditions|auth\/jwt\/(forgot-password|new-password))(\/|$)/;

export default function PlatformLegalAcceptanceGate({
  children,
}: PropsWithChildren) {
  const { user } = useAuthContext();
  if (!user?.id) return <>{children}</>;
  return <AccountPolicyGate key={user.id}>{children}</AccountPolicyGate>;
}

function AccountPolicyGate({ children }: PropsWithChildren) {
  const { logout } = useAuthContext();
  const { pathname } = useLocation();
  const { t } = useTranslate();
  const pending = usePendingPlatformPolicy();
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [guardian, setGuardian] = useState(false);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [accountOptions, setAccountOptions] = useState(false);
  const savingLock = useRef(false);
  const response = isPlatformPending(pending.data) ? pending.data : undefined;
  const valid = Boolean(response);
  const policy = response?.items[0];
  const unavailable = Boolean(pending.error || (!pending.isLoading && !valid));
  const refresh = useCallback(async () => {
    try {
      await pending.mutate();
    } catch {
      /* SWR keeps the blocking error. */
    }
  }, [pending.mutate]);
  useEffect(() => {
    setTerms(false);
    setPrivacy(false);
    setGuardian(false);
    setFailed(false);
  }, [policy?.id, policy?.checksum, policy?.locale]);
  useEffect(() => {
    const reload = () => {
      setChecking(true);
      void refresh().finally(() => setChecking(false));
    };
    window.addEventListener("platform-legal-acceptance-required", reload);
    return () =>
      window.removeEventListener("platform-legal-acceptance-required", reload);
  }, [refresh]);
  if (recoveryPath.test(pathname)) return <>{children}</>;
  if (valid && !policy && !unavailable && !pending.isLoading && !checking)
    return <>{children}</>;
  if (!policy && !unavailable && (pending.isLoading || checking))
    return (
      <LoadingScreen
        sx={{ minHeight: "100vh" }}
        role="status"
        aria-label={t("loading")}
      />
    );
  const ready = terms && privacy && (!policy?.guardian_required || guardian);
  const accept = async () => {
    if (!policy || !ready || unavailable || savingLock.current) return;
    savingLock.current = true;
    setSaving(true);
    setFailed(false);
    try {
      await acceptPlatformPolicy(
        policy,
        policy.guardian_required ? "guardian" : "self",
      );
      await refresh();
      setTerms(false);
      setPrivacy(false);
      setGuardian(false);
    } catch {
      setFailed(true);
      await refresh();
    } finally {
      savingLock.current = false;
      setSaving(false);
    }
  };
  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      disableEscapeKeyDown
      aria-labelledby="platform-legal-title"
      PaperProps={{ sx: legalDialogPaperSx }}
    >
      <DialogTitle component="div" sx={{ p: { xs: 2, sm: 3 }, flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
          <Typography
            id="platform-legal-title"
            component="h2"
            variant="h6"
            sx={{ flex: "1 1 200px" }}
          >
            {t(policy ? "platform_legal_title" : "platform_legal_error")}
          </Typography>
          <LegalLanguageSelect disabled={saving} />
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 0, px: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          {(unavailable || failed) && (
            <Alert
              severity="error"
              action={
                <Button
                  onClick={() => {
                    void refresh();
                  }}
                >
                  {t("retry")}
                </Button>
              }
            >
              {t("platform_legal_error")}
            </Alert>
          )}
          {policy && (
            <>
              <Typography variant="subtitle2">
                FoggoSport · {policy.release_key} · {policy.effective_date}
              </Typography>
              <Alert severity="info">
                {policy.release_key === "2026-10-30.1"
                  ? t("platform_legal_expansion_notice")
                  : policy.notice}
              </Alert>
              {(["terms", "privacy"] as const).map((kind) => (
                <Accordion
                  key={`${policy.id}:${policy.locale}:${kind}`}
                  defaultExpanded={kind === "terms"}
                >
                  <AccordionSummary
                    expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
                  >
                    <Typography>{policy.documents[kind].title}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      role="region"
                      aria-label={policy.documents[kind].title}
                      tabIndex={0}
                      sx={{
                        maxHeight: { xs: "30vh", md: "40vh" },
                        overflow: "auto",
                        p: 1,
                      }}
                    >
                      <LegalDocumentText>
                        {policy.documents[kind].body}
                      </LegalDocumentText>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            <Link href="/privacy-policy">{t("privacy_policy")}</Link>
            <Link href="/terms-and-conditions">
              {t("terms_and_conditions")}
            </Link>
            <Button
              size="small"
              onClick={() => setAccountOptions(!accountOptions)}
            >
              {t("comm_account_options")}
            </Button>
          </Stack>
          {accountOptions && (
            <Suspense fallback={<CircularProgress />}>
              <AccountDelete />
            </Suspense>
          )}
        </Stack>
      </DialogContent>
      <LegalAcceptanceActions
        confirmations={
          policy ? (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={terms}
                    onChange={(_, value) => setTerms(value)}
                    disabled={saving}
                  />
                }
                label={t("platform_legal_terms_confirm")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={privacy}
                    onChange={(_, value) => setPrivacy(value)}
                    disabled={saving}
                  />
                }
                label={t("platform_legal_privacy_confirm")}
              />
              {policy.guardian_required && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={guardian}
                      onChange={(_, value) => setGuardian(value)}
                      disabled={saving}
                    />
                  }
                  label={t("platform_legal_guardian_confirm")}
                />
              )}
            </>
          ) : undefined
        }
      >
        <Button
          onClick={() => {
            void logout();
          }}
          disabled={saving}
        >
          {t("playermenu_logout")}
        </Button>
        <Button
          variant="contained"
          disabled={!policy || !ready || saving || unavailable}
          onClick={() => {
            void accept();
          }}
        >
          {t("platform_legal_accept")}
        </Button>
      </LegalAcceptanceActions>
    </Dialog>
  );
}
